import { auth, functions, messaging } from '../firebase/config';
import { getToken, onMessage, MessagePayload } from 'firebase/messaging';
import { httpsCallable } from 'firebase/functions';
import { doc, setDoc, getDoc, deleteDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { DailyReminder, NotificationPermissionState, FCMTokenData } from '../types/user';
import { DateTime } from 'luxon';
import { userService } from './userService';
import { safeGetItem, safeSetItem } from '../utils/localStorageUtils';
import { createThrottle, RETENTION_THROTTLE_MS } from '../utils/retention';

export interface DashboardBannerButton {
  label: string;
  route: string;
  url: string;
}

export interface DashboardBanner {
  id: string;
  notificationId: string;
  title: string;
  body: string;
  buttons: DashboardBannerButton[];
}

// Helper to add timeout to promises
const withTimeout = <T>(promise: Promise<T>, timeoutMs: number, errorMessage: string): Promise<T> => {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(errorMessage)), timeoutMs)
    ),
  ]);
};

class NotificationService {
  private vapidKeyProduction = 'BFZyYdaWbWYFGl_0-5cErKaiAE_bYFep8q8rNHTpoEm5R09COuKfdaHVSrH4znguViqP1RLs8sBcOsYd2fEB6eA';
  private vapidKeyStaging    = 'BI4kNxZ_eZBSbpJKvLTS8c7oMesW-qBggrvL8N7l-XmoW0rv9LqmAeYE9Smgd_lKSQ3p3r7xLt80tPZ1fKcxe-8';

  private _messagingReady      = false;
  private _hasCheckedReadiness = false;
  private _retryAttempts       = 0;
  private readonly MAX_RETRY_ATTEMPTS = 3; // reduced — 5 was causing 5x SW re-registration
  private readonly RETRY_DELAY_MS    = 1500;
  private readonly FCM_TOKEN_TIMEOUT = 15000;

  private readonly allowAppOpen         = createThrottle(RETENTION_THROTTLE_MS);
  private readonly allowInsightsViewed  = createThrottle(RETENTION_THROTTLE_MS);
  private _dashboardBanners: DashboardBanner[] = [];
  private _dashboardBannersUid: string | null = null;

  // ─── KEY FIX: cache the SW registration so we never re-register ───────────
  // We resolve this ONCE via navigator.serviceWorker.ready (which gives us
  // the currently active+controlling SW — the unified sw.js) and reuse it
  // for every getToken() call. No more per-call re-registration.
  private _swRegistrationPromise: Promise<ServiceWorkerRegistration> | null = null;

  private getSWRegistration(): Promise<ServiceWorkerRegistration> {
    if (!this._swRegistrationPromise) {
      this._swRegistrationPromise = navigator.serviceWorker.ready;
    }
    return this._swRegistrationPromise;
  }
  // ──────────────────────────────────────────────────────────────────────────

  private getVapidKey(): string {
    const isProduction =
      process.env.REACT_APP_FORCE_PRODUCT_MODE === 'true' ||
      (typeof window !== 'undefined' &&
        (window.location.hostname === 'peakxv2.web.app' ||
          window.location.hostname === 'peak.app' ||
          window.location.hostname === 'www.peak.app'));
    return isProduction ? this.vapidKeyProduction : this.vapidKeyStaging;
  }

  private simpleHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(16);
  }

  getCurrentDeviceId(): string {
    const storedDeviceId = safeGetItem('peakx_device_id');
    if (storedDeviceId) return storedDeviceId;

    const userAgent         = navigator.userAgent;
    const platform          = navigator.platform;
    const language          = navigator.language;
    const screenInfo        = `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`;
    const timezone          = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const touchPoints       = navigator.maxTouchPoints || 0;
    const hardwareConcurrency = navigator.hardwareConcurrency || 0;
    const devicePixelRatio  = window.devicePixelRatio || 1;
    const deviceMemory      = (navigator as any).deviceMemory || 'unknown';

    let randomComponent = safeGetItem('peakx_device_random');
    if (!randomComponent) {
      randomComponent = Math.random().toString(36).substring(2, 10);
      safeSetItem('peakx_device_random', randomComponent);
    }

    const deviceFingerprint = [
      userAgent, platform, language, screenInfo, timezone,
      `touch:${touchPoints}`, `cores:${hardwareConcurrency}`,
      `dpr:${devicePixelRatio}`, `mem:${deviceMemory}`,
      `rand:${randomComponent}`,
    ].join('|');

    const hash     = this.simpleHash(deviceFingerprint);
    const deviceId = `dev_${hash}_${randomComponent.slice(0, 4)}`;
    safeSetItem('peakx_device_id', deviceId);
    return deviceId;
  }

  // Get device information (public method for active-session tracking)
  getDeviceInfo(): { deviceId: string; deviceName: string; platform: string; userAgent: string } {
    return this.generateDeviceInfo();
  }

  private generateDeviceInfo(): { deviceId: string; deviceName: string; platform: string; userAgent: string } {
    const userAgent = navigator.userAgent;
    const deviceId  = this.getCurrentDeviceId();

    let browserName = 'Unknown Browser';
    if (userAgent.includes('Chrome'))                              browserName = 'Chrome';
    else if (userAgent.includes('Firefox'))                        browserName = 'Firefox';
    else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) browserName = 'Safari';
    else if (userAgent.includes('Edge'))                           browserName = 'Edge';

    let osName = 'Unknown OS';
    if (userAgent.includes('Windows'))      osName = 'Windows';
    else if (userAgent.includes('Mac'))     osName = 'macOS';
    else if (userAgent.includes('Linux'))   osName = 'Linux';
    else if (userAgent.includes('Android')) osName = 'Android';
    else if (userAgent.includes('iOS'))     osName = 'iOS';

    return { deviceId, deviceName: `${browserName} on ${osName}`, platform: 'web', userAgent };
  }

  async waitForMessagingReady(): Promise<boolean> {
    if (this._messagingReady) return true;

    while (this._retryAttempts < this.MAX_RETRY_ATTEMPTS) {
      try {
        console.log(`🔄 Checking messaging readiness (attempt ${this._retryAttempts + 1}/${this.MAX_RETRY_ATTEMPTS})`);
        const token = await this.getFCMToken();
        if (token) {
          this._messagingReady      = true;
          this._hasCheckedReadiness = true;
          console.log('✅ Messaging is ready');
          return true;
        }
      } catch (error) {
        console.warn(`Messaging check failed (attempt ${this._retryAttempts + 1}):`, error);
      }

      this._retryAttempts++;
      if (this._retryAttempts < this.MAX_RETRY_ATTEMPTS) {
        await new Promise((resolve) => setTimeout(resolve, this.RETRY_DELAY_MS));
      }
    }

    console.error('❌ Messaging readiness failed after max retries');
    return false;
  }

  isMessagingReady(): boolean {
    return this._messagingReady;
  }

  resetMessagingState(): void {
    this._messagingReady          = false;
    this._hasCheckedReadiness     = false;
    this._retryAttempts           = 0;
    this._swRegistrationPromise   = null; // also reset SW registration cache
  }

  isSupported(): boolean {
    return 'Notification' in window && 'serviceWorker' in navigator && !!messaging;
  }

  getPermissionState(): NotificationPermissionState {
    if (!this.isSupported()) return { granted: false, denied: true, default: false };
    const permission = Notification.permission;
    return {
      granted: permission === 'granted',
      denied:  permission === 'denied',
      default: permission === 'default',
    };
  }

  async requestPermission(): Promise<boolean> {
    if (!this.isSupported()) throw new Error('Notifications are not supported in this browser');
    if (Notification.permission === 'granted') return true;
    if (Notification.permission === 'denied')  throw new Error('Notifications are blocked. Please enable them in your browser settings.');
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }

  // ─── FIXED getFCMToken ────────────────────────────────────────────────────
  // Before: re-registered /firebase-messaging-sw.js on EVERY call (using the
  //   wrong getRegistration() argument), binding the token to a SW that would
  //   be immediately evicted by the app SW.
  //
  // After: uses navigator.serviceWorker.ready (cached) to get the single
  //   unified sw.js registration. Token is now permanently bound to the
  //   correct, controlling SW. No re-registration ever.
  async getFCMToken(): Promise<string | null> {
    if (!messaging) {
      console.warn('Firebase messaging not initialized');
      return null;
    }

    try {
      // Wait for the unified SW to be active and in control.
      // .ready never rejects — it resolves as soon as a SW controls the page.
      const swReg = await withTimeout(
        this.getSWRegistration(),
        10000,
        'Service worker not ready within 10 seconds'
      );

      const token = await withTimeout(
        getToken(messaging, {
          vapidKey: this.getVapidKey(),
          serviceWorkerRegistration: swReg,
        }),
        this.FCM_TOKEN_TIMEOUT,
        'FCM token retrieval timed out. Please check your network connection and try again.'
      );

      const env = this.getVapidKey() === this.vapidKeyProduction ? 'PRODUCTION' : 'STAGING';
      console.log(`🔔 FCM token generated using ${env} VAPID key`);
      this._messagingReady      = true;
      this._hasCheckedReadiness = true;
      return token;
    } catch (error) {
      console.error('Failed to get FCM token:', error);
      if (error instanceof Error && error.message.includes('timed out')) throw error;
      return null;
    }
  }
  // ──────────────────────────────────────────────────────────────────────────

  async addFCMTokenToUser(userId: string, enableNotifications = true): Promise<FCMTokenData | null> {
  // REMOVED: _hasCheckedReadiness guard — it caused silent failures on app load
  // REPLACED WITH: inline readiness check that actually waits
  
  if (!this._messagingReady) {
    // console.log('[NotificationService] Messaging not ready, waiting...');
    const ready = await this.waitForMessagingReady();
    if (!ready) {
      console.warn('[NotificationService] Messaging not ready after wait, skipping token registration');
      return null;
    }
  }

 
   const token = await this.getFCMToken();
    if (!token) throw new Error('Failed to get FCM token');

    const deviceInfo = this.generateDeviceInfo();
    const now        = new Date();

    const tokenData: FCMTokenData = {
      token,
      deviceId:   deviceInfo.deviceId,
      deviceName: deviceInfo.deviceName,
      platform:   deviceInfo.platform,
      userAgent:  deviceInfo.userAgent,
      enabled:    enableNotifications,
      createdAt:  now,
      lastUsed:   now,
    };

    try {
      await userService.addFCMTokenToUser(userId, tokenData);
      console.log(`FCM token added/updated for user ${userId} on device ${deviceInfo.deviceName}`);
      return tokenData;
    } catch (error) {
      console.error('Failed to add FCM token to user:', error);
      throw error;
    }
  }

  async getEnabledFCMTokens(userId: string): Promise<string[]> {
    try {
      return await userService.getEnabledFCMTokens(userId);
    } catch (error) {
      console.error('Failed to get enabled FCM tokens:', error);
      return [];
    }
  }

  async removeInvalidFCMToken(userId: string, invalidToken: string): Promise<void> {
    try {
      await userService.removeInvalidFCMToken(userId, invalidToken);
      console.log(`Removed invalid FCM token for user ${userId}`);
    } catch (error) {
      console.error('Failed to remove invalid FCM token:', error);
    }
  }

  async toggleDeviceNotifications(userId: string, deviceId: string, enabled: boolean): Promise<void> {
    try {
      await userService.toggleDeviceNotifications(userId, deviceId, enabled);
      console.log(`${enabled ? 'Enabled' : 'Disabled'} notifications for device ${deviceId}`);
    } catch (error) {
      console.error('Failed to toggle device notifications:', error);
      throw error;
    }
  }

  async getUserFCMTokens(userId: string): Promise<FCMTokenData[]> {
    try {
      const tokens = await userService.getUserFCMTokens(userId);
      // console.log(`[NotificationService] Retrieved ${tokens.length} FCM tokens for user ${userId}`);
      return tokens;
    } catch (error) {
      console.error('Failed to get user FCM tokens:', error);
      return [];
    }
  }

  debugDeviceInfo(): void {
    console.log('=== FCM Token Debug Info ===');
    console.log('Current Device ID:', this.getCurrentDeviceId());
    console.log('Device Info:',       this.generateDeviceInfo());
    console.log('User Agent:',        navigator.userAgent);
    console.log('Platform:',          navigator.platform);
    console.log('Language:',          navigator.language);
    console.log('Screen:',            `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`);
    console.log('Timezone:',          Intl.DateTimeFormat().resolvedOptions().timeZone);
    console.log('===========================');
  }

  convertToGMT(localHour: number, timezone: string): number {
    return DateTime.now()
      .setZone(timezone)
      .set({ hour: localHour, minute: 0, second: 0, millisecond: 0 })
      .toUTC().hour;
  }

  getUserTimezone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  async saveDailyReminder(userId: string, localHour: number): Promise<void> {
    if (!messaging) throw new Error('Firebase messaging not initialized');
    if (!Number.isInteger(localHour) || localHour < 0 || localHour > 23) {
      throw new Error('localHour must be an integer between 0 and 23');
    }

    // Capture the existing bucket (if any) up front but don't delete it yet —
    // deleting before the new reminder is actually written meant a failure
    // partway through (e.g. FCM token registration) left the user with no
    // reminder at all. Write the replacement first, then clean up the old
    // bucket only once the new one is confirmed in place.
    const userRef = doc(db, 'users', userId);
    const userSnapBefore = await getDoc(userRef);
    const previousGmtHour: number | undefined = userSnapBefore.exists()
      ? userSnapBefore.data().reminderGmtHour
      : undefined;

    await this.addFCMTokenToUser(userId, true);

    const enabledTokens = await this.getEnabledFCMTokens(userId);
    if (enabledTokens.length === 0) {
      throw new Error('No enabled notification tokens found. Please enable notifications on at least one device.');
    }

    const timezone = this.getUserTimezone();
    const gmtHour  = this.convertToGMT(localHour, timezone);

    const reminderData: Omit<DailyReminder, 'createdAt' | 'updatedAt'> = {
      userId, localHour, gmtHour, timezone, enabled: true,
    };

    const reminderRef = doc(db, 'reminders', gmtHour.toString(), 'users', userId);
    await setDoc(reminderRef, {
      ...reminderData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    await setDoc(userRef, { reminderGmtHour: gmtHour, updatedAt: serverTimestamp() }, { merge: true });

    if (previousGmtHour !== undefined && previousGmtHour !== null && previousGmtHour !== gmtHour) {
      const oldReminderRef = doc(db, 'reminders', previousGmtHour.toString(), 'users', userId);
      await deleteDoc(oldReminderRef).catch((err) => {
        console.warn(`Failed to clean up previous reminder bucket (hour ${previousGmtHour}):`, err);
      });
    }

    console.log(`Reminder set for ${localHour}:00 local (${gmtHour}:00 GMT) — ${enabledTokens.length} device(s)`);
  }

  async getUserReminder(userId: string): Promise<DailyReminder | null> {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists() && userDoc.data().reminderGmtHour !== undefined) {
        const gmtHour    = userDoc.data().reminderGmtHour;
        const reminderRef = doc(db, 'reminders', gmtHour.toString(), 'users', userId);
        const reminderDoc = await getDoc(reminderRef);

        if (reminderDoc.exists()) {
          const data = reminderDoc.data();
          return {
            ...data,
            createdAt: data.createdAt?.toDate(),
            updatedAt: data.updatedAt?.toDate(),
          } as DailyReminder;
        }
      }
      return null;
    } catch (error) {
      console.error('Error fetching user reminder:', error);
      return null;
    }
  }

  async deleteReminder(userId: string): Promise<void> {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists() && userDoc.data().reminderGmtHour !== undefined) {
        const gmtHour    = userDoc.data().reminderGmtHour;
        const reminderRef = doc(db, 'reminders', gmtHour.toString(), 'users', userId);
        await deleteDoc(reminderRef);
        await updateDoc(userRef, { reminderGmtHour: null, updatedAt: serverTimestamp() });
        console.log(`Reminder deleted for GMT hour ${gmtHour}`);
        return;
      }

      // Fallback scan (backwards compatibility)
      for (let gmtHour = 0; gmtHour < 24; gmtHour++) {
        const reminderRef = doc(db, 'reminders', gmtHour.toString(), 'users', userId);
        const reminderDoc = await getDoc(reminderRef);
        if (reminderDoc.exists()) {
          await deleteDoc(reminderRef);
          await updateDoc(userRef, { reminderGmtHour: null, updatedAt: serverTimestamp() });
          console.log(`Reminder deleted for GMT hour ${gmtHour} (fallback)`);
          return;
        }
      }
    } catch (error) {
      console.error('Error deleting user reminder:', error);
    }
  }

  // ─── Retention notifications (best-effort, errors are logged only) ───────
  async recordAppOpen(): Promise<void> {
    const uid = auth.currentUser?.uid;
    if (!uid || !this.allowAppOpen(uid)) return;
    try {
      await httpsCallable<{ timezone: string }, { success: boolean }>(functions, 'recordAppOpen')({
        timezone: this.getUserTimezone(),
      });
    } catch (error) {
      console.warn('recordAppOpen failed:', error);
    }
  }

  async recordInsightsViewed(): Promise<void> {
    const uid = auth.currentUser?.uid;
    if (!uid || !this.allowInsightsViewed(uid)) return;
    try {
      await httpsCallable<Record<string, never>, { success: boolean }>(functions, 'recordInsightsViewed')({});
    } catch (error) {
      console.warn('recordInsightsViewed failed:', error);
    }
  }

  /** Last banners fetched in this session, so the dashboard can show them before the next fetch returns. */
  getCachedDashboardBanners(): DashboardBanner[] {
    return this._dashboardBannersUid === auth.currentUser?.uid ? this._dashboardBanners : [];
  }

  async getDashboardBanners(): Promise<DashboardBanner[]> {
    const uid = auth.currentUser?.uid;
    if (!uid) return [];
    this._dashboardBannersUid = uid;
    try {
      const result = await httpsCallable<{ timezone: string }, { banners?: DashboardBanner[] }>(
        functions,
        'getDashboardBanners'
      )({ timezone: this.getUserTimezone() });
      this._dashboardBanners = Array.isArray(result.data?.banners) ? result.data.banners : [];
    } catch (error) {
      console.warn('getDashboardBanners failed:', error);
      this._dashboardBanners = [];
    }
    return this._dashboardBanners;
  }

  async closeDashboardBanner(bannerId: string): Promise<void> {
    this._dashboardBanners = this._dashboardBanners.filter((banner) => banner.id !== bannerId);
    try {
      await httpsCallable<{ bannerId: string }, { success: boolean }>(functions, 'closeDashboardBanner')({ bannerId });
    } catch (error) {
      console.warn('closeDashboardBanner failed:', error);
    }
  }
  // ──────────────────────────────────────────────────────────────────────────

  setupForegroundMessageListener(callback: (payload: MessagePayload) => void): (() => void) | null {
    if (!messaging) {
      console.warn('Firebase messaging not initialized');
      return null;
    }
    return onMessage(messaging, callback);
  }
}

export const notificationService = new NotificationService();
export default notificationService;